import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Ring } from '@uiball/loaders';
import {
  LoaderOverlay,
  Tabs,
  TabButton,
  SectionSpacing,
  StepIndicator,
  FormGroup,
  Label,
  Input,
  Select,
  Hint,
  Actions,
  ActionButton,
  Message,
  PasswordTrack,
  PasswordFill,
  Guidelines,
  InputWrapper,
  ToggleButton,
} from '../styled';
import { Link } from 'react-router-dom';

export const LoginCard = forwardRef(({
  mode,
  onSwitchMode,
  step,
  totalSteps,
  email,
  password,
  otp,
  twofa,
  requiresOtp,
  requiresAuthenticator,
  requiresEmailVerification,
  onEmailChange,
  onPasswordChange,
  onOtpChange,
  onTwofaChange,
  onNext,
  onCancel,
  onResendOtp,
  message,
  messagePositive,
  loading,
  onRecaptchaChange,
  onRecaptchaExpired,
  requireRecaptcha,
  disableNext,
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const recaptchaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    resetRecaptcha: () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  }));

  return (
    <>

    <Tabs role="tablist">
      <TabButton
        type="button"
        active={mode === 'login'}
        role="tab"
        aria-selected={mode === 'login'}
        onClick={() => onSwitchMode('login')}
      >
        Log In
      </TabButton>
      <TabButton
        type="button"
        active={mode === 'signup'}
        role="tab"
        aria-selected={mode === 'signup'}
        onClick={() => onSwitchMode('signup')}
      >
        Sign Up
      </TabButton>
    </Tabs>

    <SectionSpacing>
      <LoaderOverlay visible={loading}>
        <Ring size={36} color="#5ce9ff" />
      </LoaderOverlay>

      <StepIndicator>
        Step {step} of {totalSteps}
      </StepIndicator>

      {step === 1 && (
        <FormGroup>
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={onEmailChange}
          />
        </FormGroup>
      )}

      {step === 2 && (
        <FormGroup>
          <Label htmlFor="login-password">Password</Label>
          <InputWrapper>
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={onPasswordChange}
            />
            <ToggleButton type="button" onClick={() => setShowPassword((prev) => !prev)}>
              {showPassword ? 'Hide' : 'Show'}
            </ToggleButton>
          </InputWrapper>
          <Hint>
            <Link to="/reset_password">
            Forgot password?
            </Link>
          </Hint>
        </FormGroup>
      )}

      {step === 2 && requireRecaptcha && (
        <FormGroup>
          <Label htmlFor="login-captcha">Complete the security check</Label>
          <ReCAPTCHA
            ref={recaptchaRef}
            id="login-captcha captcha-bg"
            sitekey="6LfZ2U8pAAAAAIOF3a4FMW8h7jEbm99sDVQMEKn0"
            onChange={onRecaptchaChange}
            onExpired={onRecaptchaExpired}
            className='bg-transparent'
            theme='dark'
          />
        </FormGroup>
      )}

      {step === 3 && requiresOtp && (
        <>
          {requiresEmailVerification && (
            <FormGroup>
              <Label htmlFor="login-otp-email">Email</Label>
              <Input
                id="login-otp-email"
                type="email"
                value={email}
                disabled
                style={{ opacity: 0.7 }}
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label htmlFor="login-otp">OTP Code</Label>
            <Input
              id="login-otp"
              placeholder="123456"
              value={otp}
              onChange={onOtpChange}
            />
            {requiresEmailVerification && onResendOtp && (
              <Hint>
                Didn't receive?{' '}
                <button
                  type="button"
                  onClick={onResendOtp}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#5ce9ff',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  Resend OTP
                </button>
              </Hint>
            )}
          </FormGroup>
        </>
      )}

      {step === 4 && requiresAuthenticator && (
        <FormGroup>
          <Label htmlFor="login-2fa">2FA Code</Label>
          <Input
            id="login-2fa"
            placeholder="000000"
            value={twofa}
            onChange={onTwofaChange}
          />
        </FormGroup>
      )}

      {message && (
        <Message positive={messagePositive}>
          {message}
        </Message>
      )}

      <Actions>
        <ActionButton type="button" onClick={onCancel} disabled={loading}>
          Cancel
        </ActionButton>
        <ActionButton
          type="button"
          primary
          onClick={onNext}
          disabled={loading || disableNext}
        >
          {step >= totalSteps ? 'Login' : 'Continue'}
        </ActionButton>
      </Actions>
    </SectionSpacing>
    </>
  );
});

LoginCard.displayName = 'LoginCard';

export const SignupCard = ({
  mode,
  onSwitchMode,
  step,
  totalSteps,
  role,
  onRoleChange,
  firstName,
  lastName,
  email,
  address,
  phone,
  vatNumber,
  projectCompany,
  website,
  password,
  password2,
  passwordScore,
  otp,
  onOtpChange,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onAddressChange,
  onPhoneChange,
  onVatChange,
  onProjectCompanyChange,
  onWebsiteChange,
  onPasswordChange,
  onPassword2Change,
  message,
  messagePositive,
  loading,
  onSubmit,
  onCancel,
  onResendOtp,
  onRecaptchaChange,
  onRecaptchaExpired,
  recaptchaToken,
  success,
}) => {
  const barWidth = `${(passwordScore / 4) * 100}%`;
  const barColor = passwordScore >= 3 ? '#34d399' : passwordScore === 2 ? '#f59e0b' : '#fb7185';
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupPassword2, setShowSignupPassword2] = useState(false);

  return (
    <>
      <Tabs role="tablist">
        <TabButton
          type="button"
          active={mode === 'login'}
          role="tab"
          aria-selected={mode === 'login'}
          onClick={() => onSwitchMode('login')}
        >
          Log In
        </TabButton>
        <TabButton
          type="button"
          active={mode === 'signup'}
          role="tab"
          aria-selected={mode === 'signup'}
          onClick={() => onSwitchMode('signup')}
        >
          Sign Up
        </TabButton>
      </Tabs>

      <SectionSpacing>
        <LoaderOverlay visible={loading}>
          <Ring size={36} color="#5ce9ff" />
        </LoaderOverlay>

        {step > 1 && (
          <StepIndicator>
            Step {step} of {totalSteps}
          </StepIndicator>
        )}

        {step === 1 ? (
          <form onSubmit={onSubmit}>
          <FormGroup>
            <Label htmlFor="signup-role">Role</Label>
            <Select
              id="signup-role"
              value={role}
              onChange={onRoleChange}
            >
              <option value="producer">Producer</option>
              <option value="wholesaler">Wholesaler</option>
              <option value="pharmacy">Pharmacy</option>
              <option value="hospitals">Hospitals</option>
              <option value="rsa">RSA</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-first">First Name</Label>
            <Input
              id="signup-first"
              placeholder="Your first name"
              value={firstName}
              onChange={onFirstNameChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-last">Last Name</Label>
            <Input
              id="signup-last"
              placeholder="Your last name"
              value={lastName}
              onChange={onLastNameChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={onEmailChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-address">Complete Address</Label>
            <Input
              id="signup-address"
              placeholder="Your complete address"
              value={address}
              onChange={onAddressChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-phone">Phone Number</Label>
            <Input
              id="signup-phone"
              placeholder="Your phone number"
              value={phone}
              onChange={onPhoneChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-vat">VAT Number</Label>
            <Input
              id="signup-vat"
              placeholder="Your VAT number"
              value={vatNumber}
              onChange={onVatChange}
            />
          </FormGroup>


          <FormGroup>
            <Label htmlFor="signup-password">Password</Label>
            <InputWrapper>
              <Input
                id="signup-password"
                type={showSignupPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={onPasswordChange}
              />
              <ToggleButton type="button" onClick={() => setShowSignupPassword((prev) => !prev)}>
                {showSignupPassword ? 'Hide' : 'Show'}
              </ToggleButton>
            </InputWrapper>
            <PasswordTrack>
              <PasswordFill style={{ width: barWidth, background: barColor }} />
            </PasswordTrack>
            <Hint>
              Strength: {passwordScore >= 4 ? 'strong' : passwordScore === 3 ? 'good' : passwordScore === 2 ? 'weak' : 'very weak'} (min required: GOOD)
            </Hint>
            <Guidelines>
              <li>≥ 8 characters</li>
              <li>Upper + lower case</li>
              <li>At least 1 number</li>
              <li>At least 1 symbol</li>
            </Guidelines>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-confirm">Confirm Password</Label>
            <InputWrapper>
              <Input
                id="signup-confirm"
                type={showSignupPassword2 ? 'text' : 'password'}
                placeholder="Repeat password"
                value={password2}
                onChange={onPassword2Change}
              />
              <ToggleButton type="button" onClick={() => setShowSignupPassword2((prev) => !prev)}>
                {showSignupPassword2 ? 'Hide' : 'Show'}
              </ToggleButton>
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="signup-captcha">Complete the security check</Label>
            <ReCAPTCHA
              id="signup-captcha"
              sitekey="6LfZ2U8pAAAAAIOF3a4FMW8h7jEbm99sDVQMEKn0"
              onChange={onRecaptchaChange}
              onExpired={onRecaptchaExpired}
              className="bg-transparent"
              theme="dark"
            />
          </FormGroup>

            {message && (
              <Message positive={messagePositive}>
                {message}
              </Message>
            )}

            <Actions style={{ justifyContent: 'flex-end' }}>
              <ActionButton type="submit" primary disabled={loading || !recaptchaToken}>
                {loading ? 'Creating...' : 'Create Account'}
              </ActionButton>
            </Actions>
          </form>
        ) : (
          <>
            <FormGroup>
              <Label htmlFor="signup-otp-email">Email</Label>
              <Input
                id="signup-otp-email"
                type="email"
                value={email}
                disabled
                style={{ opacity: 0.7 }}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="signup-otp">OTP Code</Label>
              <Input
                id="signup-otp"
                placeholder="Enter OTP code"
                value={otp}
                onChange={onOtpChange}
              />
              <Hint>
                Please check your email for the verification code.
              </Hint>
            </FormGroup>

            {message && (
              <Message positive={messagePositive}>
                {message}
              </Message>
            )}

            <Actions>
              {onCancel && (
                <ActionButton type="button" onClick={onCancel} disabled={loading}>
                  Cancel
                </ActionButton>
              )}
              <ActionButton
                type="button"
                primary
                onClick={onSubmit}
                disabled={loading || !otp || otp.trim().length < 4}
              >
                {loading ? 'Verifying...' : success ? 'Verified!' : 'Verify OTP'}
              </ActionButton>
            </Actions>

            <Actions style={{ justifyContent: 'center', marginTop: '1rem' }}>
              <Hint>
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={onResendOtp}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#5ce9ff',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  Resend OTP
                </button>
              </Hint>
            </Actions>
          </>
        )}
      </SectionSpacing>
    </>
  );
};

